import {
  ArrowDownward,
  ArrowUpward,
  FilterAlt,
  Search,
  Sort,
} from '@mui/icons-material';
import {
  Box,
  ClickAwayListener,
  InputBase,
  Menu,
  MenuItem,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { EnergyKind, TechSortDimension, TechSortState } from '../types';

type FilterMenuProps = {
  listOfAllOptions: readonly EnergyKind[];
  selectedOptions: readonly EnergyKind[];
  onSelect: (option: EnergyKind) => void;
  onReset: () => void;
};

export const FilterMenu: React.FC<FilterMenuProps> = ({
  listOfAllOptions,
  selectedOptions,
  onSelect,
  onReset,
}) => {
  const [anchorElement, setAnchorElement] = useState<null | SVGSVGElement>(
    null,
  );
  const isOpen = Boolean(anchorElement);

  const handleOpenMenu = (e: React.MouseEvent<SVGSVGElement>) => {
    setAnchorElement(e.currentTarget);
  };

  const handleClose = (option?: EnergyKind) => {
    setAnchorElement(null);
    if (option) {
      onSelect(option);
    }
  };

  const isFilterActive = selectedOptions.length > 0;

  return (
    <>
      <FilterAlt
        onClick={handleOpenMenu}
        sx={{
          color: 'primary.main',
          backgroundColor: isFilterActive ? 'info.main' : 'success.main',
          borderRadius: 1,
          p: 1,
          fontSize: 'inherit',
          cursor: 'pointer',
        }}
      />
      <Menu
        anchorEl={anchorElement}
        open={isOpen}
        onClose={() => handleClose()}
      >
        <MenuItem
          onClick={onReset}
          sx={{ backgroundColor: 'background.default' }}
        >
          {selectedOptions.length === 0 ? '\u2713 ' : ''}Show All
        </MenuItem>
        {listOfAllOptions.map((option) => (
          <MenuItem
            key={'' + option}
            value={option}
            onClick={() => handleClose(option)}
          >
            {selectedOptions.includes(option) ? `\u2713 ${option}` : option}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

type SortMenuProps = {
  dimensions: readonly TechSortDimension[];
  activeSort: TechSortState;
  onSortChange: (newState: TechSortState) => void;
};

export const SortMenu: React.FC<SortMenuProps> = ({
  dimensions,
  activeSort,
  onSortChange,
}) => {
  const [anchorElement, setAnchorElement] = useState<null | SVGSVGElement>(
    null,
  );
  const isOpen = Boolean(anchorElement);

  const handleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    setAnchorElement(e.currentTarget);
  };

  const handleSelect = (dim: TechSortDimension) => {
    setAnchorElement(null);
    if (dim.value === activeSort.dimension.value) {
      // Toggle direction on same dimension
      onSortChange({
        dimension: dim,
        direction: activeSort.direction === 'asc' ? 'desc' : 'asc',
      });
    } else {
      // New dimension defaults to asc
      onSortChange({ dimension: dim, direction: 'asc' });
    }
  };

  // Non-default sort = not the first dimension ascending
  const isNonDefaultSort =
    activeSort.dimension.value !== dimensions[0]?.value ||
    activeSort.direction !== 'asc';

  return (
    <>
      <Sort
        onClick={handleClick}
        sx={{
          color: 'primary.main',
          backgroundColor: isNonDefaultSort ? 'info.main' : 'success.main',
          borderRadius: 1,
          p: 1,
          fontSize: 'inherit',
          cursor: 'pointer',
        }}
      />
      <Menu
        anchorEl={anchorElement}
        open={isOpen}
        onClose={() => setAnchorElement(null)}
      >
        {dimensions.map((dim, idx) => {
          const isActive = dim.value === activeSort.dimension.value;
          return (
            <MenuItem
              key={dim.value}
              onClick={() => handleSelect(dim)}
              selected={isActive}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              {dim.name}
              {idx === 0 ? ' (default)' : ''}
              {isActive &&
                (activeSort.direction === 'asc' ? (
                  <ArrowUpward fontSize='small' sx={{ ml: 1 }} />
                ) : (
                  <ArrowDownward fontSize='small' sx={{ ml: 1 }} />
                ))}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

type SearchBoxProps = {
  query: string;
  setQuery: (str: string) => void;
};

export const SearchBox: React.FC<SearchBoxProps> = ({ query, setQuery }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const expanded = isOpen || (query && query.length > 0);

  // focus the input box if the search button has been focused
  useEffect(() => {
    if (expanded) {
      inputRef.current?.focus();
    }
  }, [expanded]);

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <Box
        onClick={() => setIsOpen(true)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'info.main',
          color: 'primary.main',
          borderRadius: 1,
          p: 2,
          minWidth: 'initial',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'action.hover',
            boxShadow: 2,
          },
        }}
      >
        <Search sx={{ height: 30, width: 30 }} />
        <InputBase
          inputRef={inputRef}
          sx={{
            flex: 1,
            width: expanded ? 200 : 0,
            transition: 'all 0.2s ease',
            px: expanded ? 2 : 0,
            overflow: 'hidden',
          }}
          placeholder='Search...'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </Box>
    </ClickAwayListener>
  );
};
